import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { reviewsService } from '../../../services/reviewsService';

const ReviewForm = ({ toUserId, jobId = null, onSuccess, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [review, setReview] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length === 0) return;

    // Limit to 5 photos total
    if (photos?.length + files?.length > 5) {
      setError('Maximum 5 photos allowed per review');
      return;
    }

    try {
      setUploadingPhotos(true);
      setError(null);

      const uploadPromises = files?.map(file => reviewsService?.uploadReviewPhoto(file));
      const uploadedPhotos = await Promise.all(uploadPromises);

      setPhotos(prev => [...prev, ...uploadedPhotos]);
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError(err?.message || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleRemovePhoto = async (photoIndex) => {
    const photo = photos?.[photoIndex];
    if (!photo) return;

    try {
      await reviewsService?.deleteReviewPhoto(photo?.path);
      setPhotos(prev => prev?.filter((_, index) => index !== photoIndex));
    } catch (err) {
      console.error('Error removing photo:', err);
      setError(err?.message || 'Failed to remove photo');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (!review?.trim()) {
      setError('Please write a review');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await reviewsService?.createReview({
        toUserId,
        jobId,
        rating,
        review: review?.trim(),
        photos: photos?.map(p => ({
          url: p?.url,
          path: p?.path,
          name: p?.name
        }))
      });

      setRating(0);
      setReview('');
      setPhotos([]);
      onSuccess?.();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-background border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Write a Review</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Star Rating */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Rating <span className="text-error">*</span>
          </label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5]?.map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="transition-transform hover:scale-110"
              >
                <Icon
                  name="Star"
                  className={`w-8 h-8 ${
                    star <= (hoveredRating || rating)
                      ? 'text-warning fill-warning' :'text-muted-foreground'
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-2 text-sm text-muted-foreground">
                {rating} out of 5 stars
              </span>
            )}
          </div>
        </div>

        {/* Review Text */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Your Review <span className="text-error">*</span>
          </label>
          <textarea
            value={review}
            onChange={(e) => setReview(e?.target?.value)}
            placeholder="Share your experience working with this person..."
            className="w-full min-h-[120px] px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            disabled={submitting}
          />
        </div>

        {/* Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Photos (Optional)
          </label>
          <div className="space-y-3">
            {/* Photo Grid */}
            {photos?.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {photos?.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo?.url}
                      alt={photo?.name || `Review photo ${index + 1}`}
                      className="w-full h-20 object-cover rounded-md border border-border"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1 right-1 bg-error text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Icon name="X" className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            {photos?.length < 5 && (
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-md hover:border-primary cursor-pointer transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhotos || submitting}
                  className="hidden"
                />
                {uploadingPhotos ? (
                  <>
                    <Icon name="Loader2" className="w-5 h-5 text-primary animate-spin" />
                    <span className="text-sm text-muted-foreground">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Camera" className="w-5 h-5 text-primary" />
                    <span className="text-sm text-foreground">
                      Add Photos ({photos?.length}/5)
                    </span>
                  </>
                )}
              </label>
            )}
            <p className="text-xs text-muted-foreground">
              Upload up to 5 photos (JPEG, PNG, WebP, GIF - max 5MB each)
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 text-error text-sm">
            <Icon name="AlertCircle" className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting || uploadingPhotos || rating === 0 || !review?.trim()}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <Icon name="Loader2" className="w-4 h-4 animate-spin" />}
            <span>{submitting ? 'Submitting...' : 'Submit Review'}</span>
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting || uploadingPhotos}
            className="px-6 py-2 border border-border rounded-md hover:bg-accent disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;